import { VTabComponent } from "@app/directives/_layout/v-tabs/v-tab.component";
import { IJourney } from "@app/interfaces/IJourney";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IPlot } from "@app/interfaces/IPlot";
import { DateService, DateUnit } from "@app/services/date.service";
import { NavigationService } from "@app/services/navigation.service";
import { TranslationService } from "@app/services/translation.service";
import { IVehicle } from "@interfaces/IVehicle";
import * as angular from "angular";
import { VideoStreamType } from "../enums/VideoStreamType";
import { ITimelineSummary } from "../models/CameraFootageTimeline.interfaces";
import { IAvailableDailyFootage } from "../models/IAvailableDailyFootage";
import { FtCloudStreamType, IVideoStream } from "../models/ICameraChannel";
import { IVehicleOnlineStatus } from "../models/IVehicleOnlineStatus";
import { IVideoEntry } from "../models/IVideoEntry";
import { IVideoRequestVerifiedPeriod } from "../models/IVideoRequest";
import { CameraFootageTimelineService } from "../services/camera-footage-timeline.service";
import { CameraFootageService } from "../services/camera-footage.service";
import { CameraService } from "../services/camera.service";
import { VideoEntryService } from "../services/video-entry.service";
import "./requestVideo.component.scss";
import { RequestVideoService } from "./requestVideo.service";

interface IComponentBindings {
    vehicle: IVehicle;
    video: IVideoEntry;
    requestVideoTime: Date;
    onlineStatus: IVehicleOnlineStatus;
}

class ComponentController implements angular.IComponentController, IComponentBindings {

    // bindings
    vehicle: IVehicle;
    video: IVideoEntry;
    requestVideoTime: Date;
    onlineStatus: IVehicleOnlineStatus;

    hideRequestControls: boolean;
    noFootageAvailable: boolean;

    cameraDevice: IPeripheral;
    isLiveStreamCamera: boolean;

    selectedDay: Date;
    videoLength: number;
    requestStartSecondOfDay: number = 0;
    requestHour: number;
    requestMinute: number;
    requestSecond: number;

    selectedChannels: IVideoStream[];
    previewStreamType: FtCloudStreamType;
    availableQualitiesAtRequestVideoTime: VideoStreamType[];
    selectedStreamType: VideoStreamType;
    substreamAvailable: boolean;

    videoRequested: boolean;
    videoPlaying: boolean;

    availableDailyFootage: IAvailableDailyFootage[];
    timelineSummary: ITimelineSummary;

    closestPlot: IPlot;
    journeys: IJourney[];

    requestLengthAdjustmentLabel: string;
    canRequestVideo: boolean;
    canPreview: boolean = true;

    // Loading
    loading: boolean;
    loadingFootage: boolean = true;
    loadingJourneys: boolean;
    seekingFootage: boolean;
    requestingVideo: boolean;
    gettingSessionData: boolean;

    currentTabIndex: number = -1;

    controlsTimeout: ReturnType<typeof setTimeout>;

    static $inject = ["$scope", "$timeout", "$mdDialog", "cameraService", "cameraFootageService", "cameraFootageTimelineService", "dateService", "navigationService", "requestVideoService", "translationService", "videoEntryService"];

    constructor(
        private readonly $scope: angular.IScope,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $mdDialog: angular.material.IDialogService,
        private readonly cameraService: CameraService,
        private readonly cameraFootageService: CameraFootageService,
        private readonly cameraFootageTimelineService: CameraFootageTimelineService,
        private readonly dateService: DateService,
        private readonly navigationService: NavigationService,
        private readonly requestVideoService: RequestVideoService,
        private readonly translationService: TranslationService,
        private readonly videoEntryService: VideoEntryService
    ) {
    }

    $onInit(): void {
        this.navigationService.updateBreadcrumbNameResolver({ vehicleVideoNameKey: this.translationService.t("requestVideo.requestVideoBreadcrumbName") });
        this.getChannelSessionData();

    }

    $onChanges(changes: angular.IOnChangesObject): void {
        if (changes['vehicle']) {
            this.setDeviceInfo();
        }

        if (changes['onlineStatus']) {
            this.setHideRequestControls();
        }

        this.$scope.$applyAsync();
    }

    $onDestroy(): void {
        clearTimeout(this.controlsTimeout);
        this.controlsTimeout = null;
    }

    onDateChanged($event: Date): void {               
        const newDate: Date = new Date($event);        

        this.checkCanRequestVideoAtTime(newDate);
        this.updateQualityControls($event);
        this.clearTrimRequestLengthLabel();

        if (!this.dateService.isSameDate(newDate, this.selectedDay) && this.isLiveStreamCamera) {
            this.loadingFootage = true;
            this.getDailyFootageFromFtCloud(newDate);

            return;
        }

        if (newDate.getTime() == this.requestVideoTime?.getTime()) {
            return;
        }

        this.updateSeekingStatus();

        if (!this.dateService.isSameDate(newDate, this.selectedDay) && (this.journeys?.length || this.availableDailyFootage?.length)) {
            this.createTimeline(newDate);
        } else {
            this.setTimes(newDate);
            this.loadingFootage = false;
        }

        this.trimRequestLengthToFitAvailableFootage();
        this.$scope.$applyAsync();
    }

    onNoAvailableDates(): void {
        this.cancelLoading();
        this.noFootageAvailable = true;
    }

    onVideoLengthChanged($event: number): void {
        this.videoLength = $event;
        this.trimRequestLengthToFitAvailableFootage();
    }

    onSelectedChannelsChanged($event: IVideoStream[]): void {
        this.selectedChannels = $event;
    }

    onNewSecondOfDaySelected($event: number): void {
        this.updateSeekingStatus();
        this.setVideoRequestTimeFromSecondOfDay($event);
    }

    onTabChange(tab: VTabComponent): void {
        this.currentTabIndex = tab?.tabIndex ?? -1;
        this.$scope.$applyAsync();
    }

    requestVideo(): void {
        this.requestingVideo = true;

        const sanitizedVideoLength: number = (this.video) ? this.videoLength : this.trimRequestLengthToFitAvailableFootage();

        this.cameraService.videoRestrictionCheck({
            VehicleId: this.vehicle.Id,
            Length: sanitizedVideoLength,
            VideoStartTime: this.requestVideoTime,
            VideoId: this.video ? this.video.Id : null,
            StreamType: this.selectedStreamType || VideoStreamType.Main
        }).then(result => {
            if (!result?.CanBeRequested) {
                const alert = this.$mdDialog
                    .alert()
                    .title(this.translationService.t(`requestVideo.VideoRequestRestrictedTitle`))
                    .ok("Ok")
                    .multiple(true)
                    .textContent(this.translationService.t(`requestVideo.VideoRequestRestrictedMessage`));

                this.$mdDialog.show(alert);

                return;
            }

            return this.videoEntryService.requestVideoUpload({
                VehicleId: this.vehicle.Id,
                Length: sanitizedVideoLength,
                VideoStartTime: this.requestVideoTime,
                Channels: this.getChannelNumbersFromChannels(),
                VideoId: this.video ? this.video.Id : null,
                VerifiedPeriod: this.getVerifiedPeriod(),
                Latitude: this.getPlotForRequest()?.Latitude,
                Longitude: this.getPlotForRequest()?.Longitude,
                StreamType: this.selectedStreamType || VideoStreamType.Main
            });

        }).finally(() => {
            this.requestingVideo = false;
            this.videoRequested = true;
        });
    }

    resetVideoRequested(): void {
        this.videoRequested = false;
        this.checkCanRequestVideoAtTime(this.requestVideoTime);
    }

    onClosestPlotFound(plot: IPlot): void {
        this.closestPlot = plot;
    }

    onJourneysUpdated(journeys: IJourney[]): void {
        this.journeys = journeys;
        this.createTimeline();
    }

    previewStateChange(eventValue: boolean): void {
        this.videoPlaying = eventValue;
    }

    timeChanged($event: Date): void {
        this.updateSeekingStatus();
        this.setTimes($event)
        this.$scope.$applyAsync();
    }

    endActiveSessions(): Promise<void> {
        return this.cameraService.closeChannelStreams(this.cameraDevice.Serial).then(() => {
            return new Promise<void>((resolve) => {
                setTimeout(() => {
                    this.getChannelSessionData();
                    resolve();
                }, 2000);
            });
        });
    }

    onDownloadQualityChanged($event: boolean): void {
        this.selectedStreamType = $event ? VideoStreamType.Sub : VideoStreamType.Main;
    }

    onFootageHistoryLoaded(availableDailyFootage: IAvailableDailyFootage[]): void {
        this.availableDailyFootage = availableDailyFootage.map(item => ({
            ...item,
            Start: new Date(item.Start),
            End: new Date(item.End)
        }));
        
        this.requestVideoTime = this.availableDailyFootage[0]?.Start;
        this.createTimeline(this.requestVideoTime);
        this.loadingFootage = false;
    }

    //--

    private updateSeekingStatus(): void {
        if (this.videoPlaying) {
            this.seekingFootage = true;
            this.$timeout(() => { this.seekingFootage = false }, 3000)
        }
    }

    private setVideoRequestTimeFromSecondOfDay(second: number): void {
        this.requestVideoTime = this.dateService.getDateTimeFromSecondOfDay(second, this.requestVideoTime);
        this.checkCanRequestVideoAtTime(this.requestVideoTime);
        this.updateQualityControls();
        this.$scope.$applyAsync();
    }

    private setHideRequestControls(): void {
        if (!this.onlineStatus) {
            return;
        }

        this.hideRequestControls = !this.onlineStatus.online && this.isLiveStreamCamera;
        this.$scope.$applyAsync();
    }

    private getDailyFootageFromFtCloud(date: Date): Promise<IAvailableDailyFootage[]> {
        if (!this.isLiveStreamCamera) {
            return Promise.resolve(null);
        }

        this.loadingFootage = true;

        return this.cameraFootageService.getFootageHistoryForDay(this.cameraDevice?.Serial, date).then(
            (footage: IAvailableDailyFootage[]) => {
                this.availableDailyFootage = footage;
                const presetTime = this.navigationService.getRouteParam("requestVideoTime");

                this.setTimes(new Date(presetTime ? presetTime : footage[0].Start));

                this.createTimeline();
                return footage;
            })
            .catch(error => {
                throw error;
            })
            .finally(() => {
                this.loadingFootage = false;
                this.$scope.$applyAsync();
            })
    }

    private setTimes(date: Date): void {
        this.requestVideoTime = date;
        this.setRequestSecondOfDay(date);
        this.selectedDay = this.dateService.getStartOfDay(date);
        this.checkCanRequestVideoAtTime(date);
    }

    private setRequestSecondOfDay(date: Date): void {
        this.requestHour = date.getHours();
        this.requestMinute = date.getMinutes();
        this.requestSecond = date.getSeconds();
        this.requestStartSecondOfDay = (((this.requestHour * 60) + this.requestMinute) * 60) + this.requestSecond;
    }   

    private createTimeline(newDate: Date = this.requestVideoTime): void {
        this.timelineSummary = null;
        this.setTimes(newDate);
        
        if (this.isLiveStreamCamera) {
            this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimeline(newDate, this.availableDailyFootage);
        }
        else if (this.availableDailyFootage?.length) {    
            const footagecopy = this.availableDailyFootage.map(item => ({
                ...item,
                Start: new Date(item.Start),
                End: new Date(item.End)
            }))
            
            this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimeline(newDate, footagecopy);            
        }
        else {
            this.timelineSummary = this.cameraFootageTimelineService.createAvailableDailyFootageTimelineFromJourneys(newDate, this.journeys);

            const journeysForRequestDate = this.journeys.filter(journey => this.dateService.isSameDate(newDate, journey.StartUtc));
            const presetTime = this.navigationService.getRouteParam("requestVideoTime");
            this.setTimes(new Date(presetTime ? presetTime : journeysForRequestDate[0].StartUtc));
            this.loadingJourneys = false;
        }

        this.updateQualityControls();

        this.checkCanRequestVideoAtTime(newDate);
        this.$scope.$applyAsync();
    }

    private cancelLoading(): void {
        this.loading = false;
        this.loadingFootage = false;
        this.loadingJourneys = false;
    }

    private clearTrimRequestLengthLabel(): void {
        this.requestLengthAdjustmentLabel = null;
    }

    private trimRequestLengthToFitAvailableFootage(): number {
        this.clearTrimRequestLengthLabel();
        const projectedEndDate: Date = this.dateService.addOrSubtractTime(this.requestVideoTime, this.videoLength, DateUnit.Sec);

        const trimmedEndDate = this.cameraFootageTimelineService.trimEndDateForRequestUsingSummary(
            this.timelineSummary,
            this.requestVideoTime,
            projectedEndDate,
            this.selectedStreamType
        );

        const endTimeFits: boolean = this.dateService.isSameDate(projectedEndDate, trimmedEndDate, true);
        const difference = Math.abs(this.dateService.getDiff(projectedEndDate, trimmedEndDate, DateUnit.Sec));

        if (!endTimeFits) {
            const endTimeString = this.dateService.formatDate(trimmedEndDate, { timeOnly: true, second: "numeric" });

            this.requestLengthAdjustmentLabel = this.translationService.t("requestVideo.VideoLengthTrimmedLabel", endTimeString);
            this.$scope.$applyAsync();
        }


        return (!endTimeFits) ? this.videoLength - Math.ceil(difference) : this.videoLength;
    }

    private getChannelNumbersFromChannels(): number[] {
        const channelNumbers: number[] = [];

        for (let i = 0; i < this.selectedChannels.length; i++) {
            channelNumbers.push(this.selectedChannels[i].ChannelId);
        }

        return channelNumbers;
    }

    private getVerifiedPeriod(): IVideoRequestVerifiedPeriod {
        if (this.video) {
            return;
        }

        const verifiedPeriod: IVideoRequestVerifiedPeriod = {};
        const endTime: Date = this.dateService.addOrSubtractTime(this.requestVideoTime, this.videoLength, DateUnit.Sec);

        for (const footage of this.timelineSummary.displaySections) {
            if (this.dateService.dateIsBetweenDates(this.requestVideoTime, footage.dateStart, footage.dateEnd)) {
                verifiedPeriod.StartTime = footage.dateStart;
            }

            if (this.dateService.dateIsBetweenDates(endTime, footage.dateStart, footage.dateEnd)) {
                verifiedPeriod.EndTime = footage.dateEnd;
            }
        }

        return verifiedPeriod;
    }


    private getPlotForRequest(): IPlot {
        if (this.closestPlot) {
            return this.closestPlot;
        }

        if (this.video) {
            return {
                Latitude: this.video.Latitude,
                Longitude: this.video.Longitude
            }
        }
    }


    private checkCanRequestVideoAtTime(newDate: Date): void {
        if (this.requestVideoService.hasFootageAvailableAtTime(newDate, this.selectedStreamType, this.timelineSummary)) {
            this.canRequestVideo = true;
            return;
        }

        this.canRequestVideo = false;
    }


    private getChannelSessionData(): void {
        if (!this.cameraDevice?.Serial) {
            return;
        }

        this.gettingSessionData = true;

        this.cameraService.peripheralHasActiveSessions(this.cameraDevice.Serial).then(hasSession => {
            this.canPreview = !hasSession;
            this.$scope.$applyAsync();
        }).finally(() => {
            this.gettingSessionData = false;
        });
    }

    private updateQualityControls(date = this.requestVideoTime): void {
        const availableFootageForRequestTime = this.requestVideoService.getFootageAvailableAtTime(date, this.timelineSummary);

        if (!availableFootageForRequestTime?.length) {
            this.selectedStreamType = VideoStreamType.Main;
            return;
        }

        const availableQualitiesSet = new Set(availableFootageForRequestTime.flatMap(footage => footage.availableVideoStreamTypes));
        this.availableQualitiesAtRequestVideoTime = [...availableQualitiesSet];

        this.substreamAvailable = this.availableQualitiesAtRequestVideoTime.includes(VideoStreamType.Sub);
        this.selectedStreamType = this.substreamAvailable ? VideoStreamType.Sub : VideoStreamType.Main;
        this.$scope.$applyAsync();
    }

    private setDeviceInfo(): void {
        this.cameraDevice = this.vehicle?.peripherals?.find(p => p.Product.IsCameraProduct);
        this.isLiveStreamCamera = this.cameraDevice.Product.ProductMetadata?.CameraFeatures?.VideoRequest?.OnlineOnly;
        this.$scope.$applyAsync();
    }
}

export class RequestVideoComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            vehicle: "<",
            requestVideoTime: "<?",
            video: "<?",
            onlineStatus: "<?"
        };

        this.controller = ComponentController;
        this.template = require("./requestVideo.component.html");
    }
}
